/**
 * Verify Credential Page
 *
 * Public page for verifying domain credentials via verification hash.
 * No authentication required - accessible to anyone with the verification link.
 */

import { useParams, Link } from 'react-router-dom';
import { useVerifyCredentialQuery } from '../store/api/credentialsApi';

export function VerifyCredentialPage() {
  const { hash } = useParams<{ hash: string }>();

  const {
    data: verification,
    isLoading,
    error,
  } = useVerifyCredentialQuery(hash!, { skip: !hash });

  if (!hash) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Invalid Verification Link</h1>
          <p className="text-slate-600">
            No verification hash provided. Please check your verification link and try again.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Verifying Credential</h1>
          <p className="text-slate-600">Please wait while we verify this credential...</p>
        </div>
      </div>
    );
  }

  // 503 VERIFICATION_UNAVAILABLE: the credential could not be READ — that
  // says nothing about whether it is valid, so it is never shown as a failure.
  if (error && (error as { status?: unknown }).status === 503) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="verify-unavailable">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Verification Temporarily Unavailable</h1>
          <p className="text-slate-600 mb-6">
            This credential could not be checked right now. This does not mean it is invalid — please try again shortly.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Verification Failed</h1>
          <p className="text-slate-600 mb-6">
            Unable to verify this credential. The verification link may be invalid or expired.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
          >
            Return to Home
            <ExternalLink className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (!verification?.isValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Credential Not Valid</h1>
          <p className="text-slate-600 mb-6">
            {verification?.reason || 'This credential could not be verified.'}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
          >
            Return to Home
            <ExternalLink className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (!verification) {
    return null;
  }

  const { credential } = verification;
  if (!credential) {
    return null;
  }

  const issuedDate = new Date(credential.issuedAt);
  const expiresDate = credential.expiresAt ? new Date(credential.expiresAt) : null;
  const isExpired = expiresDate && expiresDate < new Date();
  const isRevoked = !credential.isActive;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-8 py-6">
          <div className="flex items-center gap-4 mb-2">
            <CheckCircle className="w-12 h-12 text-white" />
            <div>
              <h1 className="text-2xl font-bold text-white">Verified Credential</h1>
              <p className="text-primary-100">BeHeard Platform</p>
            </div>
          </div>
        </div>

        {/* Credential Details */}
        <div className="px-8 py-6 space-y-6">
          {/* Status Alerts */}
          {isRevoked && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">Credential Revoked</p>
                <p className="text-sm text-red-700">This credential has been revoked and is no longer valid.</p>
              </div>
            </div>
          )}

          {isExpired && !isRevoked && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-900">Credential Expired</p>
                <p className="text-sm text-amber-700">
                  This credential expired on {expiresDate!.toLocaleDateString()}.
                </p>
              </div>
            </div>
          )}

          {!isRevoked && !isExpired && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-900">Valid Credential</p>
                <p className="text-sm text-green-700">This credential is currently active and valid.</p>
              </div>
            </div>
          )}

          {/* Credential Information */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Credential Holder</p>
                <p className="text-lg font-semibold text-slate-900">{credential.holderName}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Authority Level</p>
                <p className="text-lg font-semibold text-slate-900 capitalize">{credential.authorityLevel}</p>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <p className="text-sm font-medium text-slate-500 mb-1">Domain Certification</p>
              <p className="text-xl font-bold text-slate-900">{credential.domainLabel}</p>
              <p className="text-sm text-slate-600 capitalize mt-1">{credential.category}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Issued Date</p>
                <p className="text-slate-900">{issuedDate.toLocaleDateString()}</p>
              </div>

              {expiresDate && (
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Expiration Date</p>
                  <p className="text-slate-900">{expiresDate.toLocaleDateString()}</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="pt-6 border-t border-slate-200">
            <p className="text-xs text-slate-500 text-center">
              This credential was issued by BeHeard and can be independently verified at any time.
            </p>
            <div className="flex justify-center gap-4 mt-4">
              <Link
                to="/"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Visit BeHeard
              </Link>
              <a
                href={`https://twitter.com/intent/tweet?text=I've earned a verified ${credential.domainLabel} credential on BeHeard!`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Share on Twitter
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icon components
function CheckCircle({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function XCircle({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function AlertCircle({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function ExternalLink({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
      />
    </svg>
  );
}
